import { StateParams, StateProvider, StateService } from "@uirouter/angularjs";
import {
  ReportingServer,
  vsName as ReportingServerName,
} from "../../reporting/server";

import { vsComponentName as MachinesComponentsViewsMainName } from "./components/views/main";
import {
  getResolveObjectFromGridConfiguration,
  GridConfiguration,
  vsComponentName as RecordGridViewComponentName,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../records/views/recordGridView";
import { Machine } from "./models/machine";
import {
  FormViewController,
  FormViewService,
  vsComponentName as FormViewComponentName,
  vsServiceName as FormViewServiceName,
  FormViewConfig,
} from "../../records/forms/formView";
import {
  ChildCollection,
  CloneRecordResponse,
  IServiceResponse,
  ServiceResponse,
} from "../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import { cloneDeep } from "lodash";
import { IQService } from "angular";
import {
  CloneAndConversionService,
  vsServiceName as CloneAndConversionServiceName,
} from "./services/cloneAndConversionService";
import {
  machineConfigurationObjectName,
  machineEpromConfigurationObjectName,
  machineObjectName,
} from "../shared/names";
import { MachineEditFormViewConfig } from "./configs/machineConfiguration";
import { vsComponentName as machineDetailSpecificComponentName } from "./components/views/detail";
import { vsComponentName as machineConfigurationTypeDetailComponentName } from "./components/views/configurationType/info";
import { MachineConfigurationType } from "./models/machineConfigurationType";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import { vsComponentName as configurableGridViewComponentName } from "../../records/views/configurableGridFormView";
import {
  MachineConfiguration,
  MachineConfigurationView,
} from "./models/machineConfiguration";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../records/grids/settings/gridSettingsService";
import { GridSelectionChangedArgs } from "../../records/grids/controller";
import {
  ConfigurableGridFormViewComponentController,
  ConfigurableGridFormViewConfig,
} from "../../records/views/configurableGridFormView/controller";
import {
  MachineConfigurationCellClassService,
  vsServiceName as MachineConfigurationCellClassServiceName,
} from "../shared/machineConfigurationCellClassService";
import {
  vsServiceName as MachineConfigurableGridFormViewServiceName,
  MachineConfigurableGridFormViewService,
} from "../shared/utils/configurableRecordView";
import { vsComponentName as machineConfigurationTypeInfoGamesComponentName } from "./components/views/configurationType/info/games/component";
import { Record } from "../../records/server/crud";

import { vsObjectName as machineConfigurationTypeName } from "./configs/machineConfigurationType";
import { vsObjectName as machineConfigurationTypeGameConfigurationName } from "./configs/machineConfigurationTypeGameConfiguration";
import { vsObjectName as machineProgressiveGameObjectName } from "./configs/machineProgressiveGame";
import { vsComponentName as machineConfigurationTypeInfoProgressiveGamesComponentName } from "./components/views/configurationType/info/progressiveGames/component";
import {
  vsServiceName as GratonUtilsServiceName,
  GratonUtilsService,
} from "./utils";
import {
  ChangeJournalModalService,
  vsServiceName as ChangeJournalModalServiceName,
} from "../../records/changeJournal/changeNoteModalService";
import { getChangeJournalViewStateDefinition } from "../../records/changeJournal/changeJournalViewComponent/registration";
import { ChangeJournalObject } from "../../records/changeJournal/changeJournalViewComponent/models";
import fieldHelpers from "../../ui-formly/fieldHelpers";

export function routeConfig($stateProvider: StateProvider) {
  $stateProvider
    .state("main.machines", {
      url: "machines",
      template: "<ui-view></ui-view>",
      abstract: true,
      resolve: {
        reportGroup: [
          ReportingServerName,
          function (reportingServer: ReportingServer) {
            return reportingServer.getReportGroup("machine").then((rg) => {
              return rg;
            });
          },
        ],
      },
    })
    .state("main.machines.software", {
      url: "software",
      component: configurableGridViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          (recordGridViewService: RecordGridViewService): GridConfiguration => {
            const config = recordGridViewService.getGridConfiguration(
              machineEpromConfigurationObjectName
            )!;
            return config;
          },
        ],
        config: [
          "environment",
          "gridConfiguration",
          MachineConfigurationCellClassServiceName,
          MachineConfigurableGridFormViewServiceName,
          (
            environment: {
              Date: Date;
            },
            gridConfiguration: GridConfiguration,
            machineConfigurationCellClassService: MachineConfigurationCellClassService,
            machineConfigurableGridFormViewService: MachineConfigurableGridFormViewService
          ): ConfigurableGridFormViewConfig => {
            return {
              defaultQuery: {
                CurrentConfigurationOrDateGreaterThanOrEqualTo:
                  environment.Date,
              },
              gridConfiguration,
              additionalToggleButtons: [
                machineConfigurableGridFormViewService.futureAndCurrentConfigurationsOnlyToggleButton(
                  true
                ),
              ],
              initialized(ctrl) {
                machineConfigurationCellClassService.styleColumns(
                  ctrl.config.gridConfiguration.columns,
                  true
                );
              },
            };
          },
        ],
      },
    })
    .state("main.machines.grid", {
      url: "",
      component: MachinesComponentsViewsMainName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          "$state",
          (
            recordGridViewService: RecordGridViewService,
            $state: StateService
          ) => {
            const machineConfigurationGridConfiguration =
              recordGridViewService.getGridConfiguration(
                machineConfigurationObjectName
              );
            const gridConfiguration =
              recordGridViewService.getGridConfiguration(machineObjectName)!;

            gridConfiguration.gridDataSource =
              machineConfigurationGridConfiguration?.gridDataSource;
            gridConfiguration.onAddNew = () => {
              return $state.go("^.add").then(() => {});
            };

            return gridConfiguration;
          },
        ],
      },
    })
    .state("main.machines.add", {
      url: "/add",
      component: FormViewComponentName,
      params: {
        modelMachineId: undefined,
        modelMachineConfigurationId: undefined,
      },
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) =>
            formViewService.getFormViewConfig(machineConfigurationObjectName, <
              MachineEditFormViewConfig
            >{ machineFieldsEditable: true }),
        ],

        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) => formViewConfig.fields,
        ],

        model: [
          "$stateParams",
          CloneAndConversionServiceName,
          GratonUtilsServiceName,
          (
            $stateParams: StateParams,
            cloneAndConversionService: CloneAndConversionService,
            gratonUtilsService: GratonUtilsService
          ) => {
            const modelMachineId = $stateParams.modelMachineId;
            const modelMachineConfigurationId =
              $stateParams.modelMachineConfigurationId;

            if (!modelMachineId) {
              return {
                Configuration: {
                  Status: "Floor",
                  Eproms: gratonUtilsService.normalizeEproms([]),
                },
                Machine: {},
              };
            }

            return cloneAndConversionService.getCloneConfiguration(
              modelMachineId,
              modelMachineConfigurationId
            );
          },
        ],

        onSubmit: [
          FormViewServiceName,
          "$state",
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $state: StateService,
            changeJournalModalService: ChangeJournalModalService
          ) => [
            "formViewController",
            "model",
            (formViewController: FormViewController, model: any) => {
              // ✅ open Change Journal modal before submit
              return changeJournalModalService
                .getChangeJournalNote()
                .then((noteObj: any) => {
                  const note = noteObj?.Note;

                  // attach ChangeJournalEntry to both Machine and Configuration
                  const updatedModel = {
                    ...model,
                    Machine: { ...model.Machine },
                    Configuration: { ...model.Configuration },
                    ChangeJournalEntry: { Note: note },
                  };

                  return formViewService
                    .submitForm<ServiceResponse<Machine>>({
                      formObject: updatedModel,
                      formViewController,
                      method: "POST",
                      url: "/api/machines/Machine",
                    })
                    .then((result) =>
                      $state.go("main.machines.detail.redirect", {
                        machineId: result.Result.Id,
                      })
                    );
                });
            },
          ],
        ],

        onCancel: [
          "$state",
          ($state: StateService) => [() => $state.go("^.grid")],
        ],

        title: [() => "Add Machine"],
      },
    })

    .state("main.machines.detail", {
      url: "/{machineId:int}",
      component: machineDetailSpecificComponentName,
      resolve: {
        machine: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const machineId = $stateParams["machineId"];
            return serverUtility
              .getById<IServiceResponse<Machine>>({
                url: "/api/machines/Machine",
                id: machineId,
              })
              .then((r) => r.Result);
          },
        ],
        machineConfiguration: [
          "machine",
          (machine: Machine) =>
            machine.Configs?.find((c) => c.CurrentConfiguration),
        ],
      },
    })
    .state("main.machines.detail.redirect", {
      redirectTo: (transition) => {
        return {
          state: "main.machines.detail.configurations",
          params: transition.params(),
        };
      },
    })

    .state("main.machines.detail.edit", {
      url: "/edit/{machineConfigurationId:int}",
      component: FormViewComponentName,
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) =>
            formViewService.getFormViewConfig(machineConfigurationObjectName, <
              MachineEditFormViewConfig
            >{ machineFieldsEditable: true }),
        ],

        model: [
          "machine",
          "$stateParams",
          (machine: Machine, $stateParams: StateParams) => {
            const configurationId = $stateParams.machineConfigurationId;
            const configuration = machine.Configs?.find(
              (c) => c.Id === configurationId
            );

            const modelMachine = cloneDeep(machine);
            delete modelMachine.Configs;

            return {
              Machine: modelMachine,
              Configuration: cloneDeep(configuration),
            };
          },
        ],

        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) => formViewConfig.fields,
        ],

        onSubmit: [
          FormViewServiceName,
          "$q",
          "$state",
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $q: IQService,
            $state: StateService,
            changeJournalModalService: ChangeJournalModalService
          ) => [
            "formViewController",
            "model",
            (formViewController: FormViewController, model: any) => {
              const { form } = formViewController;

              const machineId = model.Machine.Id;
              const configurationId = model.Configuration.Id;

              // ✅ Open Change Journal modal and capture note
              return changeJournalModalService
                .getChangeJournalNote()
                .then((noteObj: any) => {
                  const note = noteObj?.Note;

                  const updatedModel = {
                    ...model,
                    Machine: {
                      ...model.Machine,
                      ChangeJournalEntry: {
                        Note: note,
                      },
                    },
                    Configuration: {
                      ...model.Configuration,
                      ChangeJournalEntry: {
                        Note: note,
                      },
                    },
                  };

                  const updatePromises = [
                    formViewService.submitForm({
                      formObject: updatedModel.Configuration,
                      formViewController: formViewController,
                      method: "PUT",
                      url: `/api/machines/MachineConfiguration/${configurationId}`,
                      validationPrefix: "Configuration",
                    }),
                  ];

                  const anyDirtyMachineFields =
                    form
                      .$getControls()
                      .find(
                        (f) => f.$name?.startsWith("Machine.") && f.$dirty
                      ) !== undefined;

                  if (anyDirtyMachineFields) {
                    updatePromises.push(
                      formViewService.submitForm({
                        formObject: updatedModel.Machine,
                        formViewController: formViewController,
                        method: "PUT",
                        url: `/api/machines/Machine/${machineId}`,
                        validationPrefix: "Machine",
                      })
                    );
                  }

                  return $q
                    .all(updatePromises)
                    .then(() =>
                      $state.go("^.redirect", { machineId }, { reload: true })
                    );
                });
            },
          ],
        ],

        onCancel: [
          "$stateParams",
          "$state",
          ($stateParams: StateParams, $state: StateService) => [
            () =>
              $state.go("^.redirect", {
                machineId: $stateParams.machineId,
              }),
          ],
        ],

        title: [() => "Edit Machine"],
      },
    })

    .state("main.machines.configurationType", {
      url: "/configurationType",
      views: {
        "@^.^": {
          component: RecordGridViewComponentName,
        },
      },
      resolve: getResolveObjectFromGridConfiguration([
        RecordGridViewServiceName,
        ColumnDefinitionServiceName,
        "$state",
        (
          recordGridViewService: RecordGridViewService,
          columnDefinitionService: ColumnDefinitionService,
          $state: StateService
        ) => {
          const config = recordGridViewService.getGridConfiguration(
            machineConfigurationTypeName
          )!;

          config.columns = columnDefinitionService.addInfoEditColumns(
            config.columns,
            { editPermission: "machineEdit", viewPermission: "machineView" }
          );
          config.onAddNew = () => $state.go(".add", {}).then(() => {});

          return config;
        },
      ]),
    })
    .state("main.machines.configurationType.add", {
      url: "/create",
      params: {
        cloneId: undefined,
      },
      views: {
        "@^.^.^": {
          component: FormViewComponentName,
        },
      },
      resolve: FormViewService.getResolveObjectFromFormViewConfig(
        [
          FormViewServiceName,
          "$state",
          ServerUtilityName,
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $state: StateService,
            serverUtility: ServerUtility,
            changeJournalModalService: ChangeJournalModalService
          ) => {
            const config = formViewService.getFormViewConfig(
              machineConfigurationTypeName
            )!;

            config.submitForm = (args) => {
              const newRecord = <MachineConfigurationType>args.model;

              newRecord.ProgressiveGames = newRecord.GameConfigs = undefined;

              return changeJournalModalService
                .getAndSetChangeJournalNote(args.model)
                .then((model) =>
                  formViewService.saveForm({
                    formObject: model,
                    formViewController: args.formViewController,
                    url: `/api/machines/MachineConfigurationType`,
                  })
                )
                .then((r: any) =>
                  $state.go("^.info.redirect", { id: r.Result.Id })
                );
            };
            return config;
          },
        ],
        [
          "$stateParams",
          ServerUtilityName,
          ($stateParams: StateParams, serverUtility: ServerUtility) => {
            const { cloneId } = $stateParams;
            if (!cloneId) return {};
            return serverUtility
              .sendApiRequest<
                ServiceResponse<CloneRecordResponse<MachineConfigurationType>>
              >({
                method: "GET",
                url: `/api/machines/MachineConfigurationType/${cloneId}/clone`,
              })
              .then((serviceResponse) => {
                const configurationType = serviceResponse.Result.Clone;
                configurationType.Name = undefined;
                configurationType.CloneId = serviceResponse.Result.Subject.Id;
                return configurationType;
              });
          },
        ]
      ),
    })

    .state("main.machines.configurationType.info", {
      url: "/{id:int}",
      views: {
        "@^.^.^": {
          component: machineConfigurationTypeDetailComponentName,
        },
      },
      resolve: {
        machineConfigurationType: [
          ServerUtilityName,
          "$stateParams",
          (serverUtility: ServerUtility, $stateParams: StateParams) =>
            serverUtility
              .getById({
                url: "/api/machines/MachineConfigurationType",
                id: $stateParams.id,
              })
              .then((r) => (<any>r).Result),
        ],
      },
    })
    .state(
      "main.machines.configurationType.info.changeLog",
      getChangeJournalViewStateDefinition({
        resolveObjects: [
          "machineConfigurationType",
          (machineConfigurationType: any): Promise<ChangeJournalObject[]> => {
            return Promise.resolve([
              {
                id: {
                  ObjectId: machineConfigurationType.Id,
                  ObjectName: "MachineConfigurationType",
                },
                object: machineConfigurationType, // or {}
              },
            ]);
          },
        ],
      })
    )

    .state("main.machines.configurationType.info.redirect", {
      url: "/redirect",
      redirectTo: (transition) => {
        return {
          state: "main.machines.configurationType.info.progressives",
          params: transition.params(),
        };
      },
    })
    .state("main.machines.configurationType.info.progressives", {
      url: "/progressives",
      component: machineConfigurationTypeInfoProgressiveGamesComponentName,
      resolve: {
        progressiveGames: [
          "machineConfigurationType",
          (machineConfigurationType: Record) =>
            machineConfigurationType.ProgressiveGames,
        ],
      },
    })
    .state("main.machines.configurationType.info.progressives.edit", {
      url: "/edit",
      views: {
        "@^.^": {
          component: FormViewComponentName,
        },
      },
      resolve: FormViewService.getResolveObjectFromFormViewConfig(
        [
          FormViewServiceName,
          "$state",
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $state: StateService,
            changeJournalModalService: ChangeJournalModalService
          ) => {
            const config = formViewService.getFormViewConfig(
              machineProgressiveGameObjectName
            )!;

            config.submitForm = (args) =>
              changeJournalModalService
                .getAndSetChangeJournalNote(args.model)
                .then((model) => {
                  const m = model as any;
                  const payload = {
                    ...m,
                    ChangeJournalEntry: m.ChangeJournalEntry,
                  };

                  return formViewService.submitForm({
                    formObject: payload,
                    formViewController: args.formViewController,
                    url: `/api/machines/MachineConfigurationType/${payload.Id}/progressiveGames`,
                    method: "PUT",
                  });
                })
                .then(() =>
                  $state.go(
                    "main.machines.configurationType.info.progressives",
                    {},
                    { reload: true }
                  )
                );

            return config;
          },
        ],
        [
          "machineConfigurationType",
          (machineConfigurationType: Record) => ({
            Id: machineConfigurationType.Id,
            Children: cloneDeep(machineConfigurationType.ProgressiveGames),
          }),
        ]
      ),
    })
    .state("main.machines.configurationType.info.games", {
      url: "/games",
      component: machineConfigurationTypeInfoGamesComponentName,
      resolve: {
        games: [
          "machineConfigurationType",
          (machineConfigurationType: Record) =>
            machineConfigurationType.GameConfigs,
        ],
      },
    })
    .state("main.machines.configurationType.info.games.edit", {
      url: "/edit",
      views: {
        "@^.^": {
          component: FormViewComponentName,
        },
      },
      resolve: FormViewService.getResolveObjectFromFormViewConfig(
        [
          FormViewServiceName,
          "$state",
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $state: StateService,
            changeJournalModalService: ChangeJournalModalService
          ) => {
            const config = formViewService.getFormViewConfig(
              machineConfigurationTypeGameConfigurationName
            )!;

            // Wrap submitForm with ChangeJournal modal
            config.submitForm = (args) =>
              changeJournalModalService
                .getAndSetChangeJournalNote(args.model)
                .then((model) => {
                  const m = model as any;
                  const payload = {
                    ...m,
                    Children: m.GameConfigs,
                    ChangeJournalEntry: m.ChangeJournalEntry,
                  };

                  return formViewService.submitForm({
                    formObject: payload,
                    formViewController: args.formViewController,
                    method: "PUT",
                    url: `/api/machines/MachineConfigurationType/${payload.Id}/gameConfigs`,
                  });
                })
                .then(() =>
                  $state.go(
                    "main.machines.configurationType.info.games",
                    undefined,
                    { reload: true }
                  )
                );

            return config;
          },
        ],
        [
          "machineConfigurationType",
          (machineConfigurationType: Record) => ({
            Id: machineConfigurationType.Id,
            GameConfigs: cloneDeep(machineConfigurationType.GameConfigs),
          }),
        ]
      ),
    })

    .state("main.machines.configurationType.info.machines", {
      url: "/machines",
      component: configurableGridViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          ColumnDefinitionServiceName,
          (
            recordGridViewService: RecordGridViewService,
            columnDefinitionService: ColumnDefinitionService
          ) => {
            const config = recordGridViewService.getGridConfiguration(
              machineConfigurationObjectName
            )!;

            config.columns = [
              columnDefinitionService.getLinkColDef({
                colId: "detail",
                headerName: " ",
                iconClass: "fa fa-info-circle text-primary",
                stateExpression:
                  "main.machines.detail.redirect({machineId: data.MachineId})",
              }),
              ...(config.columns ?? []),
            ];

            return config;
          },
        ],
        config: [
          "gridConfiguration",
          "machineConfigurationType",
          MachineConfigurationCellClassServiceName,
          "environment",
          MachineConfigurableGridFormViewServiceName,
          (
            gridConfiguration: GridConfiguration,
            machineConfigurationType: MachineConfigurationType,
            machineConfigurationCellClassService: MachineConfigurationCellClassService,
            environment: { Date: Date },
            machineConfigurableGridFormViewService: MachineConfigurableGridFormViewService
          ): ConfigurableGridFormViewConfig => {
            return {
              defaultQuery: {
                MachineConfigurationTypeId: machineConfigurationType.Id,
                CurrentConfigurationOrDateGreaterThanOrEqualTo:
                  environment.Date,
              },
              additionalToggleButtons: [
                machineConfigurableGridFormViewService.futureAndCurrentConfigurationsOnlyToggleButton(
                  true
                ),
              ],
              initialized(ctrl) {
                machineConfigurationCellClassService.styleColumns(
                  ctrl.config.gridConfiguration.columns,
                  true
                );
              },
              gridConfiguration,
              navButtonText: "Edit",
              navButtonIcon: "fa-pencil",
              navPath: ".edit",
              navButtonPermission: "machineEdit",
              showNavButton: true,
            };
          },
        ],
      },
    })
    .state("main.machines.configurationType.info.machines.edit", {
      url: "/edit",
      views: {
        "@^.^": {
          component: configurableGridViewComponentName,
        },
      },
      resolve: {
        selectedIds: [
          RecordGridViewServiceName,
          ServerUtilityName,
          "machineConfigurationType",
          (
            recordGridViewService: RecordGridViewService,
            serverUtility: ServerUtility,
            machineConfigurationType: MachineConfigurationType
          ) => {
            const machineConfigurationConfig =
              recordGridViewService.getGridConfiguration(
                machineConfigurationObjectName
              )!;
            return serverUtility
              .fetchAll<MachineConfiguration>({
                url: machineConfigurationConfig.gridDataSource!.url,
                query: {
                  MachineConfigurationTypeId: machineConfigurationType.Id,
                },
              })
              .then((result) => result.map((r) => r.MachineConfigurationId));
          },
        ],
        gridConfiguration: [
          RecordGridViewServiceName,
          GridSettingsServiceName,
          (
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService
          ) => {
            const gridConfiguration =
              recordGridViewService.getGridConfiguration(
                machineConfigurationObjectName
              )!;

            gridConfiguration.gridSettings =
              gridSettingsService.getGridSettings(
                Symbol("machineConfigurationTypeMachines.edit")
              );

            return gridConfiguration;
          },
        ],
        config: [
          ServerUtilityName,
          "machineConfigurationType",
          MachineConfigurationCellClassServiceName,
          "environment",
          "gridConfiguration",
          "selectedIds",
          MachineConfigurableGridFormViewServiceName,
          (
            serverUtility: ServerUtility,
            machineConfigurationType: MachineConfigurationType,
            machineConfigurationCellClassService: MachineConfigurationCellClassService,
            environment: { Date: Date },
            gridConfiguration: GridConfiguration,
            selectedIds: number[],
            machineConfigurableGridFormViewService: MachineConfigurableGridFormViewService
          ): ConfigurableGridFormViewConfig => {
            return {
              navButtonIcon: "fa-pencil",
              navButtonText: "Done",
              navPath: "^",
              showNavButton: true,
              showSelectButtons: true,
              defaultQuery: {
                Ids: selectedIds.length === 0 ? [0] : selectedIds,
                CurrentConfigurationOrDateGreaterThanOrEqualTo:
                  environment.Date,
              },
              additionalToggleButtons: [
                machineConfigurableGridFormViewService.idDefaultQueryToggleButton(
                  true
                ),
                machineConfigurableGridFormViewService.futureAndCurrentConfigurationsOnlyToggleButton(
                  true
                ),
              ],
              initialized: (
                controller: ConfigurableGridFormViewComponentController
              ) => {
                machineConfigurationCellClassService.styleColumns(
                  controller.config.gridConfiguration.columns,
                  true
                );
              },
              gridSelectionChanged: (args: GridSelectionChangedArgs) => {
                const machineConfiguration = <MachineConfigurationView>(
                  args.data
                );
                const machineConfigurationTypeId = machineConfigurationType.Id;
                const machineConfigurationId =
                  machineConfiguration.MachineConfigurationId;
                const url = "/api/machines/MachineConfiguration";

                serverUtility.patch<MachineConfiguration>({
                  url,
                  patchObject: {
                    Id: machineConfigurationId,
                    MachineConfigurationTypeId: args.selected
                      ? machineConfigurationTypeId
                      : undefined,
                  },
                });
              },
              gridConfiguration,
              selectedIds,
            };
          },
        ],
      },
    })
    .state("main.machines.configurationType.info.edit", {
      url: "/edit",
      component: FormViewComponentName,
      resolve: FormViewService.getResolveObjectFromFormViewConfig(
        [
          FormViewServiceName,
          "$state",
          ChangeJournalModalServiceName,
          (
            formViewService: FormViewService,
            $state: StateService,
            changeJournalModalService: ChangeJournalModalService
          ) => {
            const config = formViewService.getFormViewConfig(
              machineConfigurationTypeName
            )!;

            config.submitForm = (args) =>
              changeJournalModalService
                .getAndSetChangeJournalNote(args.model)
                .then((model) => {
                  return formViewService.saveForm({
                    formObject: model,
                    formViewController: args.formViewController,
                    url: "/api/machines/MachineConfigurationType",
                  });
                })
                .then((r: any) =>
                  $state.go(
                    "main.machines.configurationType.info.redirect",
                    { id: r.Result.Id },
                    { reload: true }
                  )
                );

            return config;
          },
        ],
        [
          "machineConfigurationType",
          (machineConfigurationType: MachineConfigurationType) => {
            machineConfigurationType.ProgressiveGames =
              machineConfigurationType.GameConfigs = undefined;
            return machineConfigurationType;
          },
        ],
        [
          "$state",
          ($state: StateService) => ["args", () => $state.go("^.redirect")],
        ]
      ),
    })
    .state("main.machines.configurationType.info.createConversions", {
      url: "/createConversions",
      component: FormViewComponentName,
      resolve: {
        machines: [
          ServerUtilityName,
          "machineConfigurationType",
          (serverUtility: ServerUtility, machineConfigurationType: Record) =>
            serverUtility
              .sendApiRequest<ServiceResponse<Record[]>>({
                url: `/api/machines/MachineConfigurationType/${machineConfigurationType.Id}/convertMachines/machines`,
                method: "get",
              })
              .then((machinesResponse) => machinesResponse.Result)
              .then((results: Machine[]) => ({
                Machines: results.map((m) => {
                  const currentConfiguration = m.Configs?.find(
                    (c) => c.CurrentConfiguration
                  );
                  return {
                    MachineId: m.Id,
                    MachineConfigurationId: currentConfiguration?.Id,
                    MachineManufacturerName: m.MachineManufacturer?.Name,
                    SerialNumber: m.SerialNumber,
                    SlotNumber: currentConfiguration?.SlotNumber,
                    Location: currentConfiguration?.Location,
                  };
                }),
              })),
        ],
        fields: [
          "machines",
          (machines: any[]) => [
            ...fieldHelpers.columnLayout(
              [
                fieldHelpers.simpleDateField({
                  key: "Date",
                  name: "Date",
                  label: "Change Date",
                  required: true,
                }),
              ],
              4
            ),
            fieldHelpers.headerField({
              headerText: "Machines",
              key: "MachinesHeader",
            }),
            {
              key: "Machines",
              model: machines,
              template:
                require("!html-loader!./components/views/main/sharedTemplates/machineList.html")
                  .default,
            },
            {
              key: "Files",
              name: "Files",
              type: "recordFileUpload",
              templateOptions: {
                allowMultiple: true,
                label: "Files",
              },
            },
            fieldHelpers.headerField({
              headerText: "Notes",
              key: "NotesHeader",
            }),
            fieldHelpers.textAreaField({
              key: "Notes",
              name: "Notes",
              label: " ",
              rows: 5,
            }),
          ],
        ],
        model: [
          "machineConfigurationType",
          (machineConfigurationType: Record) => ({
            MachineConfigurationTypeId: machineConfigurationType.Id,
          }),
        ],
        title: [() => "Convert Machines"],
        onSubmit: [
          "$state",
          FormViewServiceName,
          ChangeJournalModalServiceName,
          (
            $state: StateService,
            formViewService: FormViewService,
            changeJournalModalService: ChangeJournalModalService
          ) => [
            "formViewController",
            "model",
            (formViewController: FormViewController, model: any) => {
              const formObject = {
                ...model,
              };
              delete formObject.Machines;

              changeJournalModalService
                .getAndSetChangeJournalNote(formObject)
                .then(() =>
                  formViewService.submitForm({
                    formObject,
                    formViewController,
                    method: "post",
                    url: `/api/machines/MachineConfigurationType/${model.MachineConfigurationTypeId}/convertMachines`,
                  })
                )
                .then(() => $state.go("^.redirect"));
            },
          ],
        ],
        onCancel: [
          "$state",
          ($state: StateService) => [() => $state.go("^.redirect")],
        ],
      },
    });
}
routeConfig.$inject = ["$stateProvider"];
